import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Loader2, Calendar, Clock, Users, ChevronRight,
  CheckCircle2, AlertCircle, XCircle, CreditCard, ArrowLeft,
} from "lucide-react";

interface ReservaEncontrada {
  id: number;
  estado: string;
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  nombreCliente: string;
  sucursalNombre?: string | null;
  zonaNombre?: string | null;
  requierePagoReserva: boolean;
  ultimoPagoEstado: "pendiente" | "verificado" | "rechazado" | "anulado" | null;
}

interface SearchResult {
  total: number;
  reservas: ReservaEncontrada[];
}

function fmtFecha(s: string) {
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("es-VE", { weekday: "short", day: "numeric", month: "short" });
  } catch { return s; }
}
function fmtHora(s: string) { return (s ?? "").slice(0, 5); }

function statusBadge(r: ReservaEncontrada) {
  if (r.estado === "confirmada" || r.estado === "atendida") {
    return { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", txt: "Confirmada", Icon: CheckCircle2 };
  }
  if (r.estado === "lista_espera") {
    return { cls: "bg-violet-100 text-violet-700 border-violet-200", txt: "En lista de espera", Icon: Clock };
  }
  if (r.requierePagoReserva) {
    if (r.ultimoPagoEstado === "verificado") {
      return { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", txt: "Pago verificado", Icon: CheckCircle2 };
    }
    if (r.ultimoPagoEstado === "pendiente") {
      return { cls: "bg-amber-100 text-amber-700 border-amber-200", txt: "Pago en verificación", Icon: Clock };
    }
    if (r.ultimoPagoEstado === "rechazado") {
      return { cls: "bg-rose-100 text-rose-700 border-rose-200", txt: "Pago rechazado", Icon: XCircle };
    }
    return { cls: "bg-amber-100 text-amber-700 border-amber-200", txt: "Pago pendiente", Icon: AlertCircle };
  }
  return { cls: "bg-stone-100 text-stone-600 border-stone-200", txt: "Pendiente", Icon: Clock };
}

export default function MisReservasPage() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResult | null>(null);

  const buscar = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErr(null);
    setResult(null);
    if (q.trim().length < 3) {
      setErr("Ingresá tu cédula, email o teléfono (mínimo 3 caracteres).");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch("/api/v1/reservas/buscar-publico", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identificador: q.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.message ?? "No pudimos buscar tus reservas.");
      }
      const data: SearchResult = await r.json();
      setResult(data);
    } catch (e: any) {
      setErr(e?.message ?? "Hubo un error en la búsqueda.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">

        <Link to="/reservar"
          className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-4">
          <ArrowLeft size={14} aria-hidden /> Volver a reservar
        </Link>

        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">
            Buscá tus reservas
          </h1>
          <p className="text-sm text-stone-500 mt-1 leading-relaxed">
            Ingresá tu <b>cédula</b>, <b>email</b> o <b>teléfono</b> y te mostramos las reservas
            que tenés activas. Desde acá podés reportar el pago de cualquiera de ellas.
          </p>
        </header>

        <form onSubmit={buscar} className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
          <label htmlFor="identificador" className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-2">
            Tu cédula, email o teléfono
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="identificador"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="V12345678 · tu@email.com · 04141234567"
              className="flex-1 px-4 py-2.5 rounded-lg border border-stone-300 focus:border-stone-500 focus:outline-none text-base"
              autoFocus
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-stone-900 text-white rounded-lg font-semibold text-sm hover:bg-stone-800 disabled:opacity-60 min-h-[44px]"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Buscar
            </button>
          </div>
          {err && (
            <p className="mt-2 text-sm text-rose-600 flex items-center gap-1.5">
              <AlertCircle size={14} aria-hidden /> {err}
            </p>
          )}
        </form>

        {/* Resultados */}
        {result && (
          <section>
            {result.total === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
                <Search className="size-10 text-stone-300 mx-auto mb-3" aria-hidden />
                <p className="font-semibold text-stone-700">No encontramos reservas</p>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">
                  Probá con otro dato (cédula, email o teléfono), o
                  <Link to="/reservar" className="text-emerald-700 underline ml-1">hacé una nueva reserva</Link>.
                </p>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wider text-stone-400 font-semibold mb-2">
                  {result.total} reserva{result.total !== 1 ? "s" : ""} encontrada{result.total !== 1 ? "s" : ""}
                </p>
                <ul className="space-y-2">
                  {result.reservas.map(r => {
                    const b = statusBadge(r);
                    const BIcon = b.Icon;
                    return (
                      <li key={r.id}>
                        <Link
                          to={`/reserva/${r.id}`}
                          className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-400 hover:shadow-md transition"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-stone-900 text-base mb-1 truncate">
                                #{r.id} · {r.nombreCliente}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500">
                                {r.sucursalNombre && (
                                  <span className="font-medium text-stone-700">{r.sucursalNombre}</span>
                                )}
                                {r.zonaNombre && <span>· {r.zonaNombre}</span>}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-stone-700 mt-1.5">
                                <span className="inline-flex items-center gap-1">
                                  <Calendar size={13} aria-hidden /> {fmtFecha(r.fechaReserva)}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <Clock size={13} aria-hidden /> {fmtHora(r.horaReserva)}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <Users size={13} aria-hidden /> {r.numPersonas}
                                </span>
                              </div>
                            </div>
                            <ChevronRight size={18} className="text-stone-400 shrink-0 mt-1" aria-hidden />
                          </div>

                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-semibold ${b.cls}`}>
                              <BIcon size={11} aria-hidden /> {b.txt}
                            </span>
                            {r.requierePagoReserva && r.ultimoPagoEstado !== "verificado" && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                                <CreditCard size={11} aria-hidden /> Reportar pago →
                              </span>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        )}

        {/* Ayuda */}
        {!result && !busy && (
          <p className="text-xs text-stone-400 text-center mt-4 leading-relaxed">
            Sólo mostramos reservas <b>activas</b> (no canceladas) de los últimos 60 días o futuras.
          </p>
        )}
      </div>
    </div>
  );
}
